import React, { useState, useEffect } from 'react';
import { getSurveys, saveSurvey, deleteSurvey, getDynamicQuestions, getMemberName, getMemberAge, getCategoryForAge, getCategoryName, getMemberPhone, getMemberIICStatus, getSurveyIICStatus, getHouseholdName, getHouseholdPhone, getHouseholdLocation, getDynamicSections, getHouseholdSerialNo, getSurveyIICId, getMergedRepeaterMembers, extractStudentsFromSurvey, getUsers } from '../services/storage';
import { SurveyData, User, DynamicQuestion, SurveySection, UserRole } from '../types';
import { formatSerialNoForDisplay } from '../utils/cardUtils';
import { 
  Check, Loader, Search, Receipt, Printer, DollarSign, User as UserIcon, UserPlus,
  Clock, ChevronDown, Calendar, MapPin, Phone, AlertCircle, Sparkles, Filter, RefreshCw,
  Settings, Download, Edit
} from 'lucide-react';
import { useToast } from './Toast';

interface PaymentsManagerProps {
  currentUser: User;
}

export const PaymentsManager: React.FC<PaymentsManagerProps> = ({ currentUser }) => {
  const isViewOnly = currentUser.role === UserRole.VIEW_ONLY_ADMIN;
  const [surveys, setSurveys] = useState<SurveyData[]>([]);
  const [questions, setQuestions] = useState<DynamicQuestion[]>([]);
  const [sections, setSections] = useState<SurveySection[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  
  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'All' | 'Pending' | 'Paid'>('All');
  const [filterMember, setFilterMember] = useState<'All' | 'Member' | 'Non-Member'>('All');

  // Print Dialog State
  const [selectedSurvey, setSelectedSurvey] = useState<SurveyData | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTargetSurvey, setMergeTargetSurvey] = useState<SurveyData | null>(null);
  const [mergeSearchQuery, setMergeSearchQuery] = useState("");

  // Membership Edit State
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [membershipSurvey, setMembershipSurvey] = useState<SurveyData | null>(null);
  const [newIsMember, setNewIsMember] = useState(false);
  const [newMemberId, setNewMemberId] = useState('');
  const [savingMembership, setSavingMembership] = useState(false);
  const [isExportingPayments, setIsExportingPayments] = useState(false);

  // Amend Payment State (Master Admin Only)
  const [showAmendModal, setShowAmendModal] = useState(false);
  const [amendSurvey, setAmendSurvey] = useState<SurveyData | null>(null);
  const [amendStatus, setAmendStatus] = useState<'Paid' | 'Pending'>('Pending');
  const [amendAmount, setAmendAmount] = useState<string>('');
  const [amendPaidBy, setAmendPaidBy] = useState<string>('');
  const [amendPaidAt, setAmendPaidAt] = useState<string>('');
  const [savingAmend, setSavingAmend] = useState(false);
  const [allUsers, setAllUsers] = useState<User[]>([]);

  const { addToast } = useToast();

  const exportPaymentsCSV = () => {
    setIsExportingPayments(true);
    try {
      const escapeCsv = (str: any) => {
        if (str === undefined || str === null) return '';
        const s = String(str);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };

      const headers = [
        'Sl No',
        'Student Name',
        'Age',
        'Category',
        'Date of Birth',
        'Gender',
        'School Name',
        'Class',
        'T-Shirt Size',
        'Father\'s Name',
        'Mother\'s Name',
        'Father\'s Phone Number',
        'Mother\'s Phone Number',
        'Parent WhatsApp Number',
        'Parents Email ID',
        'Parent IIC Member',
        'Parents IIC Membership ID Number',
        'Hobbies & Interests',
        'Extracurricular Activities',
        'Attended Camps Before',
        'Details of the Camp',
        'Transportation Required',
        'Location',
        'Receipt No',
        'Amount Collected (AED)',
        'Collected By',
        'Collected At',
        'Payment Status'
      ];

      
      const exactIdMap: Record<string, string[]> = {
        dob: ['1784279365718rrpbkrt', '1782312366818', 'dob', 'dateofbirth', 'dateOfBirth'],
        gender: ['17842797575589uhslas', '1782312394122', 'gender', 'sex'],
        schoolName: ['1782312413731', 'schoolName', 'school', 'school_name'],
        className: ['1784279818028hfg1p69', '1782312452434', 'class', 'className', 'class_name', 'grade'],
        tshirt: ['1784279704280vnva2d4', 'tshirt', 't_shirt', 't-shirt', 'size'],
        fatherName: ['1784279898286bvebszx', '1782311895371', 'fatherName', 'father_name', 'father'],
        motherName: ['1784279992057yey8jtm', '1782311923394', 'motherName', 'mother_name', 'mother'],
        fatherPhone: ['178427991205860h0tpt', '1782311944131', 'fatherPhone', 'father_phone', 'fatherContact', 'father_contact', 'phone', 'mobile'],
        motherPhone: ['17842800078865yf2nt9', '1782311987788', 'motherPhone', 'mother_phone', 'motherContact', 'mother_contact'],
        whatsapp: ['1784280082279reofo98', '1782312038535', 'whatsapp', 'whatsApp'],
        email: ['1782312154158', 'email', 'emailId', 'email_id'],
        location: ['1784280188500qjb2ina', '1782312786621', 'location', 'zone', 'area', 'address'],
        hobbies: ['1782312504965', 'hobbies', 'hobby'],
        extracurricular: ['1782312533860', 'extracurricular', 'extra'],
        attendedCamps: ['1782312574481', 'attendedCamps', 'attended_camps', 'attended'],
        campDetails: ['1782312731027', 'campDetails', 'camp_details'],
        transport: ['17842801722267dyr4ba', '1782312752155', 'transport', 'transportation']
      };

      const findField = (student: any, survey: any, fieldKey: string, keywords: string[], isFamilyLevel = false) => {
        const check = (obj: any) => {
          if (!obj) return '';
          
          // 1. Check exact known template ID first
          const ids = exactIdMap[fieldKey] || [];
          for (const id of ids) {
             if (obj[id] !== undefined && obj[id] !== null) {
                if (typeof obj[id] === 'object') continue;
                const strVal = String(obj[id]).trim();
                if (strVal !== '' && strVal !== '[object Object]') {
                  return strVal;
                }
             }
          }
          
          // 2. Check by key matching keyword
          for (const key of Object.keys(obj)) {
             const val = obj[key];
             if (val !== undefined && val !== null && typeof val !== 'object') {
                const valStr = String(val).trim();
                if (valStr !== '' && valStr !== '[object Object]') {
                   const lowerKey = key.toLowerCase();
                   for (const kw of keywords) {
                      if (lowerKey === kw.toLowerCase() || lowerKey.includes(kw.toLowerCase())) {
                         return valStr;
                      }
                   }
                }
             }
          }
          
          // 3. Check by question label
          for (const q of questions) {
             const label = (q.label || '').toLowerCase();
             if (keywords.some(kw => label.includes(kw.toLowerCase()))) {
                const val = obj[q.id];
                if (val !== undefined && val !== null && typeof val !== 'object') {
                   const valStr = String(val).trim();
                   if (valStr !== '' && valStr !== '[object Object]') {
                      return valStr;
                   }
                }
             }
          }
          return '';
        };

        // Always check student object first if present
        if (student) {
          const v = check(student);
          if (v !== '') return v;
        }

        if (!isFamilyLevel) {
          return '';
        }

        if (survey) {
          const vSectionA = check(survey.sectionA);
          if (vSectionA !== '') return vSectionA;

          for (const key of Object.keys(survey)) {
             if (['id', 'submittedBy', 'submittedAt', 'paymentStatus', 'paymentAmount', 'paidBy', 'paidAt'].includes(key)) continue;
             const section = survey[key];
             if (section && typeof section === 'object' && !Array.isArray(section)) {
                const vSec = check(section);
                if (vSec !== '') return vSec;
             }
          }

          const vRoot = check(survey);
          if (vRoot !== '') return vRoot;
        }

        return '';
      };

      const cleanContactNumber = (val: any): string => {
        if (!val) return '';
        if (typeof val === 'object') return '';
        const s = String(val).trim();
        if (s === '[object Object]') return '';
        const cleaned = s.replace(/[^\d+]/g, '');
        return cleaned;
      };

      const rows: string[][] = [];

      if (filteredSurveys.length === 0) {
        addToast("No records found to export!", "warning");
        setIsExportingPayments(false);
        return;
      }

      filteredSurveys.forEach(survey => {
        const students = extractStudentsFromSurvey(survey, questions);
        
        // Family-level details (shared across all students of this survey)
        const isMemberBool = getSurveyIICStatus(survey, questions, sections);
        const isMember = isMemberBool ? 'Yes' : 'No';
        const studentRate = isMemberBool ? 150 : 200;
        const membershipId = getSurveyIICId(survey, questions) || '';
        
        // Payment-level details (shared across all students of this survey)
        const receiptNo = formatSerialNoForDisplay(getHouseholdSerialNo(survey)) || survey.id || 'N/A';
        const status = survey.paymentStatus || 'Pending';
        const collectedBy = status === 'Paid' ? (survey.paidBy || '') : '';
        const collectedAt = status === 'Paid' && survey.paidAt ? new Date(survey.paidAt).toLocaleString('en-IN') : '';

        // Process each student separately
        students.forEach(s => {
          const slNo = s.serialNo || survey.sectionA?.serialNo || survey.serialNo || '';
          const studentName = s.studentName || '';
          
          const age = getMemberAge(s, questions);
          const categoryCode = getCategoryForAge(age);
          const categoryName = getCategoryName(categoryCode);
          
          const dob = findField(s, survey, 'dob', ['dob', 'birth', 'ജനന', 'തീയതി', 'തീയ്യതി']);
          const gender = findField(s, survey, 'gender', ['gender', 'sex', 'ലിംഗം', 'ആൺ', 'പെൺ']);
          const schoolName = findField(s, survey, 'schoolName', ['school', 'സ്കൂൾ', 'വിദ്യാലയം']);
          const className = findField(s, survey, 'className', ['class', 'grade', 'ക്ലാസ്', 'ക്ലാസ്സ്']);

          const fatherName = findField(s, survey, 'fatherName', ['father', 'പിതാവ്', 'അച്ഛൻ', 'ഉപ്പ'], true);
          const motherName = findField(s, survey, 'motherName', ['mother', 'മാതാവ്', 'അമ്മ', 'ഉമ്മ'], true);
          const fatherPhone = cleanContactNumber(findField(s, survey, 'fatherPhone', ['father phone', 'father mobile', 'ഫോൺ', 'മൊബൈൽ'], true) || getHouseholdPhone(survey, questions));
          const motherPhone = cleanContactNumber(findField(s, survey, 'motherPhone', ['mother phone', 'mother mobile'], true));
          const whatsapp = cleanContactNumber(findField(s, survey, 'whatsapp', ['whatsapp', 'വാട്സാപ്പ്', 'വാട്സ്'], true));
          const email = findField(s, survey, 'email', ['email', 'ഇമെയിൽ', 'മെയിൽ'], true);
          const location = getHouseholdLocation(survey, questions);

          const hobbies = findField(s, survey, 'hobbies', ['hobby', 'hobbies', 'വിനോദം', 'താത്പര്യം']);
          const extracurricular = findField(s, survey, 'extracurricular', ['extracurricular', 'extra', 'കലാ', 'കായി']);
          
          const attendedCampsVal = findField(s, survey, 'attendedCamps', ['attended', 'camp', 'മുമ്പ്', 'പങ്കെടു']);
          const attendedCamps = attendedCampsVal === true || String(attendedCampsVal).toLowerCase() === 'yes' ? 'Yes' : 'No';
          
          const campDetails = findField(s, survey, 'campDetails', ['camp details', 'വിവരങ്ങൾ']);
          
          let amountCollected = 0;
          if (status === 'Paid') {
            if (survey.paymentAmount !== undefined) {
              amountCollected = students.length > 0 ? parseFloat((survey.paymentAmount / students.length).toFixed(2)) : studentRate;
            } else {
              amountCollected = studentRate;
            }
          } else {
            amountCollected = 0;
          }

          const tshirtSize = findField(s, survey, 'tshirt', ['tshirt', 't-shirt', 'size']);
          const transVal = findField(s, survey, 'transport', ['transport', 'यात्र', 'യാത്ര', 'സൗകര്യം', 'വാഹനം'], true);
          const transRequired = transVal === true || String(transVal).toLowerCase() === 'yes' ? 'Yes' : 'No';

          rows.push([
            escapeCsv(slNo),
            escapeCsv(studentName),
            escapeCsv(isNaN(age) ? '' : age),
            escapeCsv(categoryName || categoryCode || 'N/A'),
            escapeCsv(dob),
            escapeCsv(gender),
            escapeCsv(schoolName),
            escapeCsv(className),
            escapeCsv(tshirtSize),
            escapeCsv(fatherName),
            escapeCsv(motherName),
            escapeCsv(fatherPhone),
            escapeCsv(motherPhone),
            escapeCsv(whatsapp),
            escapeCsv(email),
            escapeCsv(isMember),
            escapeCsv(membershipId),
            escapeCsv(hobbies),
            escapeCsv(extracurricular),
            escapeCsv(attendedCamps),
            escapeCsv(campDetails),
            escapeCsv(transRequired),
            escapeCsv(location),
            escapeCsv(receiptNo),
            escapeCsv(amountCollected),
            escapeCsv(collectedBy),
            escapeCsv(collectedAt),
            escapeCsv(status)
          ]);
        });
      });

      const csvContent = "\uFEFF" + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `payments_report_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      addToast("Payments report exported successfully!", "success");
    } catch (err: any) {
      console.error(err);
      addToast("Failed to export payments report", "error");
    } finally {
      setIsExportingPayments(false);
    }
  };

  const updateMembershipStatus = (survey: SurveyData, isMember: boolean, memberId?: string) => {
    const updatedSurvey = { ...survey };
    if (!updatedSurvey.sectionA) {
      updatedSurvey.sectionA = {};
    } else {
      updatedSurvey.sectionA = { ...updatedSurvey.sectionA };
    }

    const statusStr = isMember ? 'Yes' : 'No';
    const statusBool = isMember;

    // 1. Update priority keys in sectionA and at root
    const priorityKeys = ['isIICMember', 'iicMember', 'membership', 'isMember', 'iic_member', 'memberStatus', '1782312069160'];
    priorityKeys.forEach(key => {
      if (updatedSurvey.sectionA) {
        updatedSurvey.sectionA[key] = key === 'iicMember' || key === '1782312069160' ? statusStr : statusBool;
      }
      updatedSurvey[key] = key === 'iicMember' || key === '1782312069160' ? statusStr : statusBool;
    });

    // 2. Scan questions for any IIC membership question and set it
    questions.forEach(q => {
      const labelLower = String(q.label || '').toLowerCase();
      const idLower = String(q.id).toLowerCase();
      if (
        labelLower.includes('iic') || 
        labelLower.includes('member') || 
        idLower.includes('iic') || 
        idLower.includes('member')
      ) {
        if (q.type === 'yesno' || q.type === 'dropdown') {
          updatedSurvey.sectionA[q.id] = statusStr;
          updatedSurvey[q.id] = statusStr;
        } else {
          updatedSurvey.sectionA[q.id] = statusBool;
          updatedSurvey[q.id] = statusBool;
        }
      }
    });

    // 3. Update Membership ID if provided and member is true
    if (isMember && memberId) {
      const idKeys = ['membershipId', 'memberId', 'iicId', 'iicNumber', 'membershipNumber', 'memberNumber', 'cardId', 'idNumber', 'iic_id', 'membership_id', 'iicMemberId', 'iic_member_id', '1782312123373'];
      idKeys.forEach(key => {
        if (updatedSurvey.sectionA) {
          updatedSurvey.sectionA[key] = memberId;
        }
        updatedSurvey[key] = memberId;
      });

      // Also scan questions for ID question
      questions.forEach(q => {
        const labelLower = String(q.label || '').toLowerCase();
        if ((labelLower.includes('iic') || labelLower.includes('member')) && (labelLower.includes('id') || labelLower.includes('no') || labelLower.includes('number'))) {
          updatedSurvey.sectionA[q.id] = memberId;
          updatedSurvey[q.id] = memberId;
        }
      });
    } else if (!isMember) {
      // Clear Membership IDs
      const idKeys = ['membershipId', 'memberId', 'iicId', 'iicNumber', 'membershipNumber', 'memberNumber', 'cardId', 'idNumber', 'iic_id', 'membership_id', 'iicMemberId', 'iic_member_id', '1782312123373'];
      idKeys.forEach(key => {
        if (updatedSurvey.sectionA) {
          delete updatedSurvey.sectionA[key];
        }
        delete updatedSurvey[key];
      });

      // Also scan questions for ID question to clear them
      questions.forEach(q => {
        const labelLower = String(q.label || '').toLowerCase();
        if ((labelLower.includes('iic') || labelLower.includes('member')) && (labelLower.includes('id') || labelLower.includes('no') || labelLower.includes('number'))) {
          if (updatedSurvey.sectionA) {
            delete updatedSurvey.sectionA[q.id];
          }
          delete updatedSurvey[q.id];
        }
      });
    }

    return updatedSurvey;
  };

  const handleToggleMembershipClick = (survey: SurveyData) => {
    setMembershipSurvey(survey);
    const isCurrentlyMember = getSurveyIICStatus(survey, questions, sections);
    setNewIsMember(isCurrentlyMember);
    setNewMemberId(getSurveyIICId(survey, questions) || '');
    setShowMembershipModal(true);
  };

  const handleSaveMembership = async () => {
    if (!membershipSurvey) return;
    setSavingMembership(true);
    try {
      let updatedSurvey = updateMembershipStatus(membershipSurvey, newIsMember, newMemberId);
      
      // Recalculate and apply discount instantly
      const { total } = calculateAmount(updatedSurvey);
      if (updatedSurvey.paymentStatus === 'Paid') {
        updatedSurvey.paymentAmount = total;
      }

      await saveSurvey(updatedSurvey);
      
      setSurveys(prev => prev.map(s => s.id === updatedSurvey.id ? updatedSurvey : s));
      addToast('Membership status updated. Fee discount applied!', 'success');
      setShowMembershipModal(false);
    } catch (err: any) {
      console.error(err);
      addToast(err.message || 'Failed to update membership status', 'error');
    } finally {
      setSavingMembership(false);
    }
  };

  const handleResetPayment = async (survey: SurveyData) => {
    const code = window.prompt("Please enter the revocation code to revoke this payment:");
    if (code !== "ZUHA") {
      alert("Invalid code. Please contact admin to revoke this payment.");
      return;
    }
    if (!window.confirm("Are you sure you want to revoke this payment? This will mark it as Unpaid and clear the collection details.")) {
      return;
    }
    setUpdatingId(survey.id);
    try {
      const updated: SurveyData = {
        ...survey,
        paymentStatus: 'Pending',
        paidBy: undefined,
        paidAt: undefined,
        paymentAmount: undefined
      };
      
      await saveSurvey(updated);
      setSurveys(prev => prev.map(s => s.id === survey.id ? updated : s));
      addToast('Payment reset to Pending successfully!', 'success');
    } catch (err) {
      console.error(err);
      addToast('Failed to reset payment status', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAmendPaymentClick = (survey: SurveyData) => {
    setAmendSurvey(survey);
    setAmendStatus(survey.paymentStatus === 'Paid' ? 'Paid' : 'Pending');
    const { total } = calculateAmount(survey);
    setAmendAmount(survey.paymentAmount !== undefined ? String(survey.paymentAmount) : String(total));
    setAmendPaidBy(survey.paidBy || currentUser.name || currentUser.username || '');
    setAmendPaidAt(survey.paidAt ? new Date(survey.paidAt).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16));
    setShowAmendModal(true);
  };

  const handleSaveAmend = async () => {
    if (!amendSurvey) return;
    setSavingAmend(true);
    try {
      const parsedAmount = parseFloat(amendAmount);
      if (isNaN(parsedAmount) || parsedAmount < 0) {
        throw new Error('Please enter a valid positive payment amount');
      }

      const updated: SurveyData = {
        ...amendSurvey,
        paymentStatus: amendStatus,
        paymentAmount: amendStatus === 'Paid' ? parsedAmount : undefined,
        paidBy: amendStatus === 'Paid' ? amendPaidBy : undefined,
        paidAt: amendStatus === 'Paid' ? new Date(amendPaidAt).toISOString() : undefined
      };

      await saveSurvey(updated);
      setSurveys(prev => prev.map(s => s.id === amendSurvey.id ? updated : s));
      addToast('Payment record amended successfully!', 'success');
      setShowAmendModal(false);
    } catch (err: any) {
      console.error(err);
      addToast(err.message || 'Failed to amend payment details', 'error');
    } finally {
      setSavingAmend(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [allSurveys, allQuestions, allSections, fetchedUsers] = await Promise.all([
        getSurveys(),
        getDynamicQuestions(),
        getDynamicSections(),
        currentUser.role === UserRole.MASTER_ADMIN ? getUsers() : Promise.resolve([])
      ]);
      setSurveys(allSurveys);
      setQuestions(allQuestions);
      setSections(allSections);
      setAllUsers(fetchedUsers);
    } catch (err) {
      console.error(err);
      addToast('Failed to load payment registrations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStudentsForSurvey = (survey: SurveyData) => {
    return extractStudentsFromSurvey(survey, questions);
  };

  const calculateAmount = (survey: SurveyData) => {
    const students = getStudentsForSurvey(survey);
    const count = students.length;
    
    // Use centralized robust membership check
    const isMember = getSurveyIICStatus(survey, questions, sections);

    const subtotal = count * 200;
    const discount = isMember ? count * 50 : 0;
    const manualSiblingCount = survey.manualSiblingDiscountCount || 0;
    const extraSiblingDiscount = (count > 1 ? count * 25 : 0) + (manualSiblingCount * 25);
    const total = Math.max(0, subtotal - discount - extraSiblingDiscount);
    return { count, subtotal, discount, extraSiblingDiscount, manualSiblingCount, total, isMember };
  };

  
  const handleMergeSurveys = async (sourceSurvey: SurveyData) => {
    if (!mergeTargetSurvey) return;
    setUpdatingId(mergeTargetSurvey.id);
    try {
      const updated = { ...mergeTargetSurvey };
      
      // Merge repeater sections
      Object.keys(sourceSurvey).forEach(key => {
        const sourceVal = sourceSurvey[key];
        if (Array.isArray(sourceVal)) {
          const targetVal = updated[key] || [];
          if (Array.isArray(targetVal)) {
            updated[key] = [...targetVal, ...sourceVal];
          } else {
            updated[key] = [...sourceVal];
          }
        }
      });

      if (updated.paymentStatus === 'Paid') {
         const { total } = calculateAmount(updated);
         updated.paymentAmount = total;
      }

      await saveSurvey(updated);
      await deleteSurvey(sourceSurvey.id);

      setSurveys(prev => prev.map(s => s.id === updated.id ? updated : s).filter(s => s.id !== sourceSurvey.id));
      addToast('Students merged successfully', 'success');
      setShowMergeModal(false);
      setMergeTargetSurvey(null);
    } catch (e) {
      console.error(e);
      addToast('Failed to merge records', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAddSiblingDiscount = async (survey: SurveyData, increment: number) => {
    setUpdatingId(survey.id);
    try {
      const currentCount = survey.manualSiblingDiscountCount || 0;
      const newCount = Math.max(0, currentCount + increment);
      const updated: SurveyData = {
        ...survey,
        manualSiblingDiscountCount: newCount
      };
      
      // If already paid, we also need to update payment amount? The user might just want to apply it before paying.
      if (survey.paymentStatus === 'Paid') {
         const { total } = calculateAmount(updated);
         updated.paymentAmount = total;
      }
      
      await saveSurvey(updated);
      setSurveys(prev => prev.map(s => s.id === survey.id ? updated : s));
      addToast(increment > 0 ? 'Sibling discount added' : 'Sibling discount removed', 'success');
    } catch (e) {
      console.error(e);
      addToast('Failed to update sibling discount', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleMarkAsPaid = async (survey: SurveyData) => {
    setUpdatingId(survey.id);
    try {
      const { total } = calculateAmount(survey);
      const updated: SurveyData = {
        ...survey,
        paymentStatus: 'Paid',
        paymentAmount: total,
        paidBy: currentUser.name || currentUser.username,
        paidAt: new Date().toISOString()
      };
      
      await saveSurvey(updated);
      
      // Update local state
      setSurveys(prev => prev.map(s => s.id === survey.id ? updated : s));
      addToast('Payment marked as Paid successfully!', 'success');
      
      // Auto open print receipt dialog
      setSelectedSurvey(updated);
      setShowReceiptModal(true);
    } catch (err) {
      console.error(err);
      addToast('Failed to update payment status', 'error');
    } finally {
      setUpdatingId(null);
    }
  };

  // Printing trigger
  const handlePrint = () => {
    window.print();
  };

  // Filter surveys
  const filteredSurveys = surveys.filter(s => {
    const term = searchQuery.toLowerCase().trim();
    
    // Priority search using extracted students
    const checkPriorityMatch = (): boolean => {
      if (!term) return true;
      const students = extractStudentsFromSurvey(s, questions);
      
      // Priority 1: Parent phone number search
      const hasPhoneMatch = students.some(stud => stud.phone && String(stud.phone).toLowerCase().includes(term));
      if (hasPhoneMatch) return true;

      // Priority 2: Father's / Mother's name search
      const hasParentMatch = students.some(stud => stud.parentName && stud.parentName.toLowerCase().includes(term));
      if (hasParentMatch) return true;

      // Priority 3: Student name search
      const hasStudentMatch = students.some(stud => stud.studentName && stud.studentName.toLowerCase().includes(term));
      if (hasStudentMatch) return true;

      return false;
    };

    const checkValueFallback = (val: any): boolean => {
      if (val === undefined || val === null) return false;
      if (typeof val === 'string') {
        if (val.startsWith('data:image')) return false;
        return val.toLowerCase().includes(term);
      }
      if (typeof val === 'number') return String(val).includes(term);
      if (typeof val === 'boolean') return (val ? 'yes' : 'no').includes(term);
      if (Array.isArray(val)) return val.some(checkValueFallback);
      if (typeof val === 'object') return Object.values(val).some(checkValueFallback);
      return false;
    };

    const queryMatch = searchQuery === '' || checkPriorityMatch() || checkValueFallback(s);

    const statusVal = s.paymentStatus || 'Pending';
    const statusMatch = filterStatus === 'All' || statusVal === filterStatus;

    const isMember = getSurveyIICStatus(s, questions, []);
    const memberMatch = filterMember === 'All' || 
      (filterMember === 'Member' && isMember) || 
      (filterMember === 'Non-Member' && !isMember);

    return queryMatch && statusMatch && memberMatch;
  });

  return (
    <div className="space-y-6">
      {/* Print CSS Styles specifically for 80mm Receipt */}
      <style>{`
        @media print {
          /* Hide everything except the receipt */
          body * {
            visibility: hidden !important;
          }
          #thermal-receipt-print, #thermal-receipt-print * {
            visibility: visible !important;
          }
          #thermal-receipt-print {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm !important;
            margin: 0 !important;
            padding: 4mm !important;
            box-shadow: none !important;
            border: none !important;
            background: white !important;
            color: black !important;
            font-family: 'Courier New', Courier, monospace !important;
            font-size: 13px !important;
            line-height: 1.2 !important;
          }
          /* Prevent paper waste / unwanted margins */
          @page {
            size: auto;
            margin: 0mm;
          }
        }
      `}</style>

      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <h2 className="text-2xl font-black text-gray-900 flex items-center gap-2">
            <DollarSign className="text-emerald-600 bg-emerald-100 p-1.5 rounded-lg w-9 h-9" />
            Payments Panel
          </h2>
          <p className="text-gray-500 mt-1">Manage summer camp fees, mark payments, and print 80mm thermal receipts.</p>
        </div>
        <div className="flex gap-3 bg-emerald-50 px-4 py-2.5 rounded-xl border border-emerald-100">
          <Sparkles className="text-emerald-600 animate-pulse mt-0.5" size={16} />
          <div className="text-xs">
            <p className="font-bold text-emerald-800">Summer Camp 2026</p>
            <p className="text-emerald-600">Malayalee Samajam, Abu Dhabi</p>
          </div>
        </div>
      </div>

      {/* Filters and search card */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search parent name, phone, serial or student name..."
                className="w-full bg-gray-50 border border-gray-200 pl-11 pr-4 py-3 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-emerald-500 text-gray-900 placeholder:text-gray-400 transition-all text-sm"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <button 
              onClick={loadData}
              disabled={loading}
              className="bg-white border border-gray-200 p-3 rounded-xl text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 transition-all active:scale-95 disabled:opacity-50"
              title="Refresh Data"
            >
              <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
          
          <div className="flex flex-wrap gap-3">
            {/* Status Filter */}
            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5">
              <span className="text-xs font-bold text-gray-500 uppercase">Payment:</span>
              <select
                className="bg-transparent font-semibold text-gray-700 outline-none text-sm cursor-pointer"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value as any)}
              >
                <option value="All">All Status</option>
                <option value="Pending">Pending Only</option>
                <option value="Paid">Paid Only</option>
              </select>
            </div>

            {/* Member Filter */}
            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5">
              <span className="text-xs font-bold text-gray-500 uppercase">Member:</span>
              <select
                className="bg-transparent font-semibold text-gray-700 outline-none text-sm cursor-pointer"
                value={filterMember}
                onChange={e => setFilterMember(e.target.value as any)}
              >
                <option value="All">All Members</option>
                <option value="Member">Malayalee Samajam Members</option>
                <option value="Non-Member">Non-Members</option>
              </select>
            </div>

            {/* Export Payments CSV Button */}
            <button
              type="button"
              onClick={exportPaymentsCSV}
              disabled={loading || filteredSurveys.length === 0 || isExportingPayments}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 text-white border border-emerald-600 px-4.5 py-1.5 rounded-xl font-bold text-sm flex items-center gap-2 transition-all active:scale-95 shadow-sm hover:shadow"
              title="Export filtered registrations and payment details to CSV"
            >
              {isExportingPayments ? (
                <Loader className="animate-spin" size={16} />
              ) : (
                <Download size={16} />
              )}
              {isExportingPayments ? 'Exporting...' : 'Export Payments CSV'}
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-2 border-t border-gray-100">
          <div className="bg-gray-50 p-3 rounded-xl border border-gray-100 text-center">
            <p className="text-xs font-bold text-gray-400 uppercase">Total Submissions</p>
            <p className="text-xl font-extrabold text-gray-800 mt-0.5">{surveys.length}</p>
          </div>
          <div className="bg-amber-50/50 p-3 rounded-xl border border-amber-100 text-center">
            <p className="text-xs font-bold text-amber-500 uppercase">Pending Payments</p>
            <p className="text-xl font-extrabold text-amber-800 mt-0.5">
              {surveys.filter(s => (s.paymentStatus || 'Pending') === 'Pending').length}
            </p>
          </div>
          <div className="bg-emerald-50/50 p-3 rounded-xl border border-emerald-100 text-center">
            <p className="text-xs font-bold text-emerald-600 uppercase">
              {currentUser.role === UserRole.PAYMENT_ADMIN ? 'My Completed Payments' : 'Completed Payments'}
            </p>
            <p className="text-xl font-extrabold text-emerald-800 mt-0.5">
              {surveys.filter(s => {
                if (s.paymentStatus !== 'Paid') return false;
                if (currentUser.role === UserRole.PAYMENT_ADMIN) {
                  return s.paidBy === currentUser.name || s.paidBy === currentUser.username;
                }
                return true;
              }).length}
            </p>
          </div>
          <div className="bg-blue-50/50 p-3 rounded-xl border border-blue-100 text-center">
            <p className="text-xs font-bold text-blue-500 uppercase">
              {currentUser.role === UserRole.PAYMENT_ADMIN ? 'My Total Collected' : 'Total Collected'}
            </p>
            <p className="text-xl font-extrabold text-blue-800 mt-0.5">
              {surveys
                .filter(s => {
                  if (s.paymentStatus !== 'Paid') return false;
                  if (currentUser.role === UserRole.PAYMENT_ADMIN) {
                    return s.paidBy === currentUser.name || s.paidBy === currentUser.username;
                  }
                  return true;
                })
                .reduce((acc, curr) => acc + (curr.paymentAmount || 0), 0)} AED
            </p>
          </div>
        </div>

        {currentUser.role === UserRole.MASTER_ADMIN && (
          <div className="pt-4 border-t border-gray-100 mt-4">
            <h4 className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Admin Collection Breakdown</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(
                surveys
                  .filter(s => s.paymentStatus === 'Paid')
                  .reduce<Record<string, { count: number, total: number }>>((acc, s) => {
                    const adminName = s.paidBy || 'Unknown Admin';
                    if (!acc[adminName]) {
                      acc[adminName] = { count: 0, total: 0 };
                    }
                    acc[adminName].count += 1;
                    acc[adminName].total += (s.paymentAmount || 0);
                    return acc;
                  }, {})
              ).map(([adminName, data]) => (
                <div key={adminName} className="bg-emerald-50/40 border border-emerald-100/60 p-3.5 rounded-xl flex items-center justify-between">
                  <div className="truncate pr-2">
                    <p className="font-extrabold text-sm text-gray-800 truncate">{adminName}</p>
                    <p className="text-[10px] font-bold text-gray-400 uppercase mt-0.5">{data.count} {data.count === 1 ? 'receipt' : 'receipts'} closed</p>
                  </div>
                  <div className="text-right font-black text-emerald-800 text-sm whitespace-nowrap">
                    {data.total.toFixed(2)} AED
                  </div>
                </div>
              ))}
              {surveys.filter(s => s.paymentStatus === 'Paid').length === 0 && (
                <p className="text-xs text-gray-400 italic">No payments collected yet.</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Registrations List */}
      {loading ? (
        <div className="flex h-64 items-center justify-center bg-white rounded-2xl shadow-sm border border-gray-100">
          <Loader className="animate-spin text-emerald-600" size={32} />
        </div>
      ) : filteredSurveys.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl border-2 border-dashed border-gray-100 text-center space-y-3">
          <AlertCircle className="text-gray-300 w-12 h-12" />
          <h3 className="text-lg font-bold text-gray-700">No payment records found</h3>
          <p className="text-gray-400 text-sm max-w-sm">No registrations match your search criteria. Try removing filters or changing search keywords.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredSurveys.map(survey => {
            const { count, subtotal, discount, extraSiblingDiscount, total, isMember } = calculateAmount(survey);
            const status = survey.paymentStatus || 'Pending';
            const membershipId = getSurveyIICId(survey, questions) || '';
            
            return (
              <div 
                key={survey.id} 
                className={`bg-white rounded-2xl shadow-sm border transition-all hover:shadow-md p-6 ${
                  status === 'Paid' ? 'border-emerald-100 bg-emerald-50/5' : 'border-gray-200'
                }`}
              >
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  {/* Parent Details */}
                  <div className="space-y-1.5 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-md font-bold">
                        {formatSerialNoForDisplay(survey.sectionA?.serialNo) || 'No Serial'}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        isMember ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {isMember ? 'Malayalee Samajam Member (150 AED/std)' : 'Non-Member (200 AED/std)'}
                      </span>
                      {!isMember && (
                        <span className="bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded text-xs font-bold font-mono border border-amber-200">
                          No Membership ID
                        </span>
                      )}
                      {isMember && membershipId && (
                        <span className="bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded text-xs font-bold font-mono border border-blue-200">
                          ID: {membershipId}
                        </span>
                      )}
                      {!isViewOnly && (
                        <button
                          onClick={() => handleToggleMembershipClick(survey)}
                          className="text-[11px] text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 px-2.5 py-1 rounded-lg font-bold flex items-center gap-1 transition-all"
                          title="Change membership and apply dynamic discount"
                        >
                          {isMember ? <Settings size={12} /> : <UserPlus size={12} />}
                          {isMember ? 'Update Membership' : 'Add Member Benefit'}
                        </button>
                      )}
                    </div>
                    
                    <h3 className="text-lg font-black text-gray-800">{getHouseholdName(survey, questions) || 'Unnamed Household'}</h3>
                    
                    <div className="flex flex-wrap gap-y-1 gap-x-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Phone size={13} /> {getHouseholdPhone(survey, questions) || 'N/A'}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin size={13} /> {getHouseholdLocation(survey, questions) || 'N/A'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar size={13} /> {new Date(survey.submittedAt).toLocaleDateString()}
                      </span>
                    </div>

                    {/* Registered Students */}
                    <div className="mt-3 bg-gray-50 rounded-xl p-3 border border-gray-100 space-y-1.5 max-w-xl">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Registered Students ({count})</p>
                      {count === 0 ? (
                        <p className="text-xs text-gray-400 italic">No family members registered as students.</p>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {getStudentsForSurvey(survey).map((m: any, idx: number) => {
                            const name = m.studentName || getMemberName(m, questions);
                            const age = getMemberAge(m, questions);
                            const category = getCategoryForAge(age);
                            return (
                              <div key={`${m.id || 'student'}-${idx}`} className="text-xs bg-white p-2 rounded-lg border border-gray-100 flex items-center justify-between">
                                <span className="font-bold text-gray-700 truncate mr-1">{idx+1}. {name}</span>
                                <span className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-black flex-shrink-0">
                                  {getCategoryName(category)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Calculations & Pricing Panel */}
                  <div className="flex flex-col sm:flex-row lg:flex-col items-stretch sm:items-center lg:items-end justify-between w-full lg:w-auto gap-4 border-t lg:border-t-0 pt-4 lg:pt-0 border-gray-100">
                    <div className="text-right space-y-1 sm:w-48 lg:w-auto">
                      <div className="text-xs text-gray-400">
                        {count} × 200 AED = {subtotal} AED
                      </div>
                      {isMember ? (
                        <div className="text-xs text-emerald-600 font-bold">
                          Member Discount: -{discount} AED
                        </div>
                      ) : (
                        <div className="text-xs text-amber-600 font-bold">
                          Standard Rate (No Member Discount)
                        </div>
                      )}
                      {extraSiblingDiscount > 0 && (
                        <div className="text-xs text-indigo-600 font-bold">
                          Sibling Discount: -{extraSiblingDiscount} AED
                        </div>
                      )}
                      <div className="text-xl font-black text-emerald-800">
                        Total: {total} AED
                      </div>
                      {!isMember && !isViewOnly && (
                        <button
                          onClick={() => handleToggleMembershipClick(survey)}
                          className="text-[10px] text-indigo-600 hover:text-indigo-800 font-extrabold flex items-center gap-1 mt-1 transition-all lg:self-end"
                        >
                          ⚡ Add Member Benefit (Save {count * 50} AED)
                        </button>
                      )}
                      
                      {!isViewOnly && (
                        <div className="flex items-center gap-1 mt-1 lg:self-end">
                           <button
                             onClick={() => {
                               setMergeTargetSurvey(survey);
                               setShowMergeModal(true);
                             }}
                             disabled={updatingId === survey.id}
                             className="text-[10px] text-purple-600 hover:text-purple-800 font-extrabold transition-all bg-purple-50 px-2 py-0.5 rounded"
                             title="Merge with a sibling registered separately"
                           >
                             Merge Sibling
                           </button>
                           
                           <button
                             onClick={() => handleAddSiblingDiscount(survey, 1)}
                             disabled={updatingId === survey.id}
                             className="text-[10px] text-indigo-600 hover:text-indigo-800 font-extrabold transition-all bg-indigo-50 px-2 py-0.5 rounded"
                             title="Manually apply a 25 AED discount"
                           >
                             + Sibling Disc (-25)
                           </button>
                           {(survey.manualSiblingDiscountCount || 0) > 0 && (
                             <button
                               onClick={() => handleAddSiblingDiscount(survey, -1)}
                               disabled={updatingId === survey.id}
                               className="text-[10px] text-red-600 hover:text-red-800 font-extrabold transition-all bg-red-50 px-2 py-0.5 rounded"
                             >
                               - Remove
                             </button>
                           )}
                        </div>
                      )}
                    </div>

                    {/* Status & Actions */}
                    <div className="flex items-center gap-2">
                      {status === 'Paid' ? (
                        <div className="flex flex-col items-end">
                          <span className="bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-xl font-bold text-sm flex items-center gap-1">
                            <Check size={16} /> Paid
                          </span>
                          <span className="text-[10px] text-gray-400 mt-1 max-w-[140px] text-right truncate">
                            By {survey.paidBy || 'Admin'}
                          </span>
                        </div>
                      ) : (
                        <span className="bg-amber-100 text-amber-800 px-3 py-1.5 rounded-xl font-bold text-sm flex items-center gap-1">
                          <Clock size={16} /> Unpaid
                        </span>
                      )}

                      {/* Print Receipt Button (if Paid, or can always show) */}
                      {status === 'Paid' ? (
                        <div className="flex gap-2">
                          {!isViewOnly && (
                            <button
                              onClick={() => {
                                setSelectedSurvey(survey);
                                setShowReceiptModal(true);
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white p-2.5 rounded-xl transition-all shadow hover:shadow-md"
                              title="Print Thermal Receipt"
                            >
                              <Printer size={18} />
                            </button>
                          )}
                          {!isViewOnly && (
                            <button
                              onClick={() => handleResetPayment(survey)}
                              disabled={updatingId === survey.id}
                              className="bg-red-50 hover:bg-red-100 text-red-600 p-2.5 rounded-xl border border-red-200 transition-all shadow hover:shadow-md"
                              title="Revoke Payment (Admin)"
                            >
                              <RefreshCw size={18} className={updatingId === survey.id ? 'animate-spin' : ''} />
                            </button>
                          )}
                          {currentUser.role === UserRole.MASTER_ADMIN && (
                            <button
                              onClick={() => handleAmendPaymentClick(survey)}
                              disabled={updatingId === survey.id}
                              className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2.5 rounded-xl border border-blue-200 transition-all shadow hover:shadow-md"
                              title="Amend Payment Details (Master Admin)"
                            >
                              <Edit size={18} />
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          {!isViewOnly && (
                            <button
                              onClick={() => handleMarkAsPaid(survey)}
                              disabled={updatingId === survey.id}
                              className="bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white font-extrabold px-4 py-2.5 rounded-xl transition-all shadow hover:shadow-md text-sm flex items-center gap-2"
                            >
                              {updatingId === survey.id ? (
                                <Loader className="animate-spin" size={16} />
                              ) : (
                                <DollarSign size={16} />
                              )}
                              Mark as Paid
                            </button>
                          )}
                          {currentUser.role === UserRole.MASTER_ADMIN && (
                            <button
                              onClick={() => handleAmendPaymentClick(survey)}
                              disabled={updatingId === survey.id}
                              className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2.5 rounded-xl border border-blue-200 transition-all shadow hover:shadow-md"
                              title="Amend Payment Details (Master Admin)"
                            >
                              <Edit size={18} />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 80mm Thermal Receipt Preview Modal */}
      {showReceiptModal && selectedSurvey && (() => {
        const { count, subtotal, discount, extraSiblingDiscount, total, isMember } = calculateAmount(selectedSurvey);
        return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full relative space-y-4 no-print border border-gray-100">
            <div className="flex justify-between items-center pb-2 border-b">
              <h3 className="text-lg font-black text-gray-800 flex items-center gap-2">
                <Receipt className="text-emerald-600" />
                Receipt Preview
              </h3>
              <button 
                onClick={() => setShowReceiptModal(false)}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-1.5 rounded-full"
              >
                &times;
              </button>
            </div>

            {/* Simulated Thermal Paper Roll Container */}
            <div className="bg-gray-100 p-4 rounded-2xl flex justify-center">
              {/* Thermal Print Receipt (This matches 80mm width paper) */}
              <div 
                id="thermal-receipt-print"
                className="bg-white shadow-lg p-8 w-[320px] border border-gray-200 text-black text-left leading-normal min-h-[600px] font-sans"
              >
                <div className="text-center border-b-[3px] border-black pb-4 mb-4">
                  <h3 className="text-2xl font-black uppercase tracking-tighter leading-none mb-1">Malayalee Samajam</h3>
                  <p className="text-base font-black uppercase tracking-widest text-gray-800">വേനൽ പറവകൾ - 2026</p>
                  <div className="mt-4 pt-3 border-t border-dashed border-gray-300">
                    <p className="text-[10px] font-black uppercase text-gray-400">Approved Admin: <span className="text-gray-900">{selectedSurvey.paidBy || currentUser.name || 'Admin'}</span></p>
                    <p className="text-[9px] font-bold text-gray-400 font-mono italic">
                       TIME: {selectedSurvey.paidAt ? new Date(selectedSurvey.paidAt).toLocaleTimeString() : new Date().toLocaleTimeString()}
                    </p>
                  </div>
                </div>

                <div className="mb-6 py-2 bg-gray-50 rounded flex justify-center">
                   <div className="text-center font-mono text-xl border-2 border-black p-2 mt-2">
                     {formatSerialNoForDisplay(getHouseholdSerialNo(selectedSurvey)) || (selectedSurvey.id || 'PAID')}
                   </div>
                </div>

                <div className="space-y-1 text-xs mb-6">
                  <div className="flex justify-between items-baseline">
                    <span className="font-black text-gray-500 uppercase text-[10px]">Family / Parent</span>
                    <span className="font-black uppercase truncate ml-2 max-w-[140px] text-right">{getHouseholdName(selectedSurvey, questions) || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="font-black text-gray-500 uppercase text-[10px]">Contact</span>
                    <span className="font-black uppercase">{getHouseholdPhone(selectedSurvey, questions) || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="font-black text-gray-500 uppercase text-[10px]">Location</span>
                    <span className="font-black uppercase">{getHouseholdLocation(selectedSurvey, questions) || 'Unknown'}</span>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <span className="font-black text-gray-500 uppercase text-[10px]">Malayalee Samajam Member</span>
                    <span className="font-black uppercase">{isMember ? 'YES' : 'NO'}</span>
                  </div>
                  {isMember && (
                    <div className="flex justify-between border-t border-dotted border-gray-400 pt-2 mt-1">
                      <span className="font-black text-gray-500 uppercase text-[10px]">Member ID</span>
                      <span className="font-black uppercase">{getSurveyIICId(selectedSurvey, questions) || 'Verified'}</span>
                    </div>
                  )}
                </div>

                <div className="border-t-[3px] border-black pt-3 mb-6">
                  <p className="text-xs font-black uppercase tracking-widest mb-3 border-b-2 border-gray-100 pb-2">Registered Students</p>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-2 uppercase font-black">Description</th>
                        <th className="text-right py-2 uppercase font-black">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {getStudentsForSurvey(selectedSurvey).map((m, idx) => {
                        const studentName = m.studentName || getMemberName(m, questions) || `Student ${idx + 1}`;
                        const age = getMemberAge(m, questions);
                        const category = getCategoryForAge(age);
                        return (
                          <tr key={m.id || idx}>
                            <td className="py-3 pr-2">
                              <p className="font-black leading-tight uppercase text-sm">{idx + 1}. {studentName}</p>
                              <p className="text-[10px] text-emerald-700 font-black uppercase">ID: {m.serialNo || m.idNo || 'PND'}</p>
                              <p className="text-[9px] text-gray-400 font-bold uppercase mt-0.5">{category ? getCategoryName(category) : 'Camp Registration'}</p>
                            </td>
                            <td className="py-3 text-right font-black text-sm">200.00</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                    </div>
                    <div className="border-t-[3px] border-black pt-4 space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="font-black uppercase text-gray-500">Subtotal</span>
                        <span className="font-black">{subtotal.toFixed(2)} AED</span>
                      </div>
                      {isMember ? (
                        <div className="flex justify-between text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1.5 rounded border border-emerald-100 font-black">
                          <span className="uppercase italic text-[10px]">Malayalee Samajam Member Rate</span>
                          <span className="text-[10px]">-{discount.toFixed(2)} AED</span>
                        </div>
                      ) : (
                        <div className="flex justify-between text-xs text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded border border-amber-200 font-black">
                          <span className="uppercase italic text-[10px]">Standard Registration Rate</span>
                        </div>
                      )}
                      {extraSiblingDiscount > 0 && (
                        <div className="flex justify-between text-xs text-indigo-700 bg-indigo-50 px-2.5 py-1.5 rounded border border-indigo-100 font-black mt-1">
                          <span className="uppercase italic text-[10px]">Sibling Discount</span>
                          <span className="text-[10px]">-{extraSiblingDiscount.toFixed(2)} AED</span>
                        </div>
                      )}
                      <div className="flex justify-between text-xl font-black border-t-[3px] border-double border-black pt-3 mt-3">
                        <span className="uppercase tracking-tighter text-emerald-700">Total Paid</span>
                        <span className="text-emerald-700">
                          {(selectedSurvey.paymentAmount || total).toFixed(2)} AED
                        </span>
                      </div>
                    </div>
                                
                <div className="mt-10 pt-6 border-t-2 border-dashed border-gray-300 text-center space-y-2">
                   <p className="font-black text-lg tracking-[0.3em] text-emerald-700 uppercase">*** PAID ***</p>
                   <p className="text-[11px] font-black text-gray-800 uppercase leading-snug tracking-tight">
                     Thank you for registering your child!<br/>
                     Official Receipt | വേനൽ പറവകൾ - 2026
                   </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handlePrint}
                className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white font-extrabold py-3 rounded-2xl flex items-center justify-center gap-2 transition-all shadow hover:shadow-lg"
              >
                <Printer size={18} />
                Print (80mm)
              </button>
              <button
                onClick={() => setShowReceiptModal(false)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-extrabold py-3 rounded-2xl transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
        );
      })()}
      
      {/* Merge Modal */}
      {showMergeModal && mergeTargetSurvey && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-lg w-full relative space-y-4 border border-gray-100">
            <div className="flex justify-between items-center pb-2 border-b">
              <h3 className="text-lg font-black text-gray-800">Merge & Apply Sibling Discount</h3>
              <button 
                onClick={() => {
                  setShowMergeModal(false);
                  setMergeTargetSurvey(null);
                  setMergeSearchQuery("");
                }}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-1.5 rounded-full"
              >
                &times;
              </button>
            </div>
            <div className="space-y-4">
               <p className="text-sm text-gray-600">Select another registration to merge with <strong>{getHouseholdName(mergeTargetSurvey, questions) || mergeTargetSurvey.id}</strong>. Their students will be added here and the sibling discount will be applied.</p>
               
               <input
                 type="text"
                 placeholder="Search by Parent Name, Student Name, Serial or Phone..."
                 value={mergeSearchQuery}
                 onChange={e => setMergeSearchQuery(e.target.value)}
                 className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
               />
               
               <div className="max-h-60 overflow-y-auto space-y-2">
                 {surveys
                   .filter(s => s.id !== mergeTargetSurvey.id)
                   .filter(s => {
                     if (!mergeSearchQuery) return true;
                     const q = mergeSearchQuery.toLowerCase();
                     const parent = (getHouseholdName(s, questions) || '').toLowerCase();
                     const phone = (getHouseholdPhone(s, questions) || '').toLowerCase();
                     const serial = (getHouseholdSerialNo(s) || '').toLowerCase();
                     const students = getStudentsForSurvey(s).map((m: any) => (m.studentName || getMemberName(m, questions) || '').toLowerCase()).join(' ');
                     return parent.includes(q) || phone.includes(q) || serial.includes(q) || students.includes(q);
                   })
                   .map(s => (
                     <div key={s.id} className="border p-3 rounded-xl flex justify-between items-center hover:bg-gray-50">
                       <div>
                         <p className="font-bold text-sm">{getHouseholdName(s, questions) || 'Unknown Parent'}</p>
                         <p className="text-xs text-gray-500">Phone: {getHouseholdPhone(s, questions) || 'N/A'}</p>
                         <p className="text-xs text-emerald-600 font-bold">{getStudentsForSurvey(s).length} Student(s)</p>
                       </div>
                       <button
                         onClick={() => {
                           if (window.confirm("Are you sure you want to merge these registrations? The selected registration will be deleted.")) {
                             handleMergeSurveys(s);
                           }
                         }}
                         className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700"
                       >
                         Merge
                       </button>
                     </div>
                   ))
                 }
               </div>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Membership Status Edit Modal */}
      {showMembershipModal && membershipSurvey && (() => {
        const testUpdatedSurvey = updateMembershipStatus(membershipSurvey, newIsMember, newMemberId);
        const projected = calculateAmount(testUpdatedSurvey);
        const hName = getHouseholdName(membershipSurvey, questions) || 'Household';
        const serial = formatSerialNoForDisplay(membershipSurvey.sectionA?.serialNo) || 'No Serial';
        
        return (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full relative space-y-4 border border-gray-100">
              <div className="flex justify-between items-center pb-2 border-b">
                <h3 className="text-lg font-black text-gray-800 flex items-center gap-2">
                  <Settings className="text-blue-600" />
                  Update Membership
                </h3>
                <button 
                  onClick={() => setShowMembershipModal(false)}
                  className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-1.5 rounded-full"
                >
                  &times;
                </button>
              </div>

              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-1">
                <p className="text-xs font-bold text-gray-400 uppercase">Guardian / Household</p>
                <p className="font-extrabold text-gray-800 text-base">{hName}</p>
                <p className="text-xs font-mono text-gray-500 font-bold">Serial No: {serial}</p>
              </div>

              <div className="space-y-4">
                <label className="block text-xs font-black text-gray-400 uppercase tracking-wider mb-2">Membership Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setNewIsMember(true)}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      newIsMember 
                        ? 'border-emerald-600 bg-emerald-50/40 text-emerald-900 shadow-sm' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-500'
                    }`}
                  >
                    <p className="font-black text-sm">Malayalee Samajam Member</p>
                    <p className="text-[10px] opacity-85 mt-1">150 AED / student</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewIsMember(false)}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      !newIsMember 
                        ? 'border-red-600 bg-red-50/40 text-red-900 shadow-sm' 
                        : 'border-gray-200 hover:border-gray-300 text-gray-500'
                    }`}
                  >
                    <p className="font-black text-sm">Non-Member</p>
                    <p className="text-[10px] opacity-85 mt-1">200 AED / student</p>
                  </button>
                </div>

                {newIsMember && (
                  <div className="space-y-1 animate-fadeIn">
                    <label className="block text-xs font-black text-gray-400 uppercase tracking-wider">Malayalee Samajam Membership ID Number</label>
                    <input
                      type="text"
                      placeholder="e.g. MS-2026-9999"
                      value={newMemberId}
                      onChange={e => setNewMemberId(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-emerald-500 text-gray-900 font-semibold text-sm transition-all"
                    />
                  </div>
                )}
              </div>

              {/* Projected calculations strip */}
              <div className="bg-blue-50/50 p-4 rounded-2xl border border-blue-100/60 space-y-2">
                <p className="text-xs font-black text-blue-800 uppercase tracking-wider">Projected Fee Calculation</p>
                <div className="flex justify-between text-xs text-gray-600 font-semibold">
                  <span>Registered Students:</span>
                  <span className="font-bold">{projected.count}</span>
                </div>
                <div className="flex justify-between text-xs text-gray-600 font-semibold">
                  <span>Rate per student:</span>
                  <span className="font-bold">{newIsMember ? '150.00 AED' : '200.00 AED'}</span>
                </div>
                <div className="flex justify-between text-sm font-black text-blue-900 border-t border-blue-100 pt-2 mt-1">
                  <span>New Total Fee:</span>
                  <span>{projected.total.toFixed(2)} AED</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSaveMembership}
                  disabled={savingMembership || (newIsMember && !newMemberId.trim())}
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white font-extrabold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-all shadow hover:shadow-lg"
                >
                  {savingMembership && <Loader className="animate-spin" size={16} />}
                  Save Changes
                </button>
                <button
                  onClick={() => setShowMembershipModal(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-extrabold py-3.5 rounded-2xl transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Amend Payment Modal (Master Admin Only) */}
      {showAmendModal && amendSurvey && (() => {
        const hName = getHouseholdName(amendSurvey, questions) || 'Household';
        const serial = formatSerialNoForDisplay(amendSurvey.sectionA?.serialNo) || 'No Serial';
        
        return (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-md w-full relative space-y-4 border border-gray-100">
              <div className="flex justify-between items-center pb-2 border-b">
                <h3 className="text-lg font-black text-gray-800 flex items-center gap-2">
                  <Edit className="text-blue-600" />
                  Amend Payment
                </h3>
                <button 
                  onClick={() => setShowAmendModal(false)}
                  className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-1.5 rounded-full"
                >
                  &times;
                </button>
              </div>

              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-1">
                <p className="text-xs font-bold text-gray-400 uppercase">Guardian / Household</p>
                <p className="font-extrabold text-gray-800 text-base">{hName}</p>
                <p className="text-xs font-mono text-gray-500 font-bold">Serial No: {serial}</p>
              </div>

              <div className="space-y-4">
                {/* Status selection */}
                <div>
                  <label className="block text-xs font-black text-gray-400 uppercase tracking-wider mb-2">Payment Status</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setAmendStatus('Paid')}
                      className={`p-3.5 rounded-2xl border-2 text-center transition-all ${
                        amendStatus === 'Paid'
                          ? 'border-emerald-600 bg-emerald-50/40 text-emerald-900 font-bold shadow-sm' 
                          : 'border-gray-200 hover:border-gray-300 text-gray-500 font-semibold'
                      }`}
                    >
                      Paid
                    </button>
                    <button
                      type="button"
                      onClick={() => setAmendStatus('Pending')}
                      className={`p-3.5 rounded-2xl border-2 text-center transition-all ${
                        amendStatus === 'Pending'
                          ? 'border-amber-600 bg-amber-50/40 text-amber-900 font-bold shadow-sm' 
                          : 'border-gray-200 hover:border-gray-300 text-gray-500 font-semibold'
                      }`}
                    >
                      Pending (Unpaid)
                    </button>
                  </div>
                </div>

                {amendStatus === 'Paid' && (
                  <>
                    {/* Amount */}
                    <div className="space-y-1">
                      <label className="block text-xs font-black text-gray-400 uppercase tracking-wider">Collected Amount (AED)</label>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        placeholder="e.g. 150"
                        value={amendAmount}
                        onChange={e => setAmendAmount(e.target.value)}
                        className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-emerald-500 text-gray-900 font-semibold text-sm transition-all"
                      />
                    </div>

                    {/* Collected By */}
                    <div className="space-y-1">
                      <label className="block text-xs font-black text-gray-400 uppercase tracking-wider">Collected By (Admin Name)</label>
                      <select
                        value={allUsers.some(u => (u.name || u.username) === amendPaidBy) ? amendPaidBy : "custom"}
                        onChange={e => {
                          const val = e.target.value;
                          if (val === "custom") {
                            setAmendPaidBy("");
                          } else {
                            setAmendPaidBy(val);
                          }
                        }}
                        className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-emerald-500 text-gray-900 font-semibold text-sm transition-all"
                      >
                        <option value="">-- Select Admin/User --</option>
                        {allUsers.map(u => {
                          const displayName = u.name || u.username;
                          return (
                            <option key={u.id || displayName} value={displayName}>
                              {displayName} ({u.role})
                            </option>
                          );
                        })}
                        <option value="custom">Custom Name / Other...</option>
                      </select>

                      {/* Show custom input if "custom" is selected or if amendPaidBy doesn't match any registered user */}
                      {(!allUsers.some(u => (u.name || u.username) === amendPaidBy) || amendPaidBy === "") && (
                        <input
                          type="text"
                          placeholder="Enter Custom Admin Name"
                          value={amendPaidBy}
                          onChange={e => setAmendPaidBy(e.target.value)}
                          className="w-full mt-2 bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-emerald-500 text-gray-900 font-semibold text-sm transition-all animate-in fade-in slide-in-from-top-1"
                        />
                      )}
                    </div>

                    {/* Collected At */}
                    <div className="space-y-1">
                      <label className="block text-xs font-black text-gray-400 uppercase tracking-wider">Collected At (Date & Time)</label>
                      <input
                        type="datetime-local"
                        value={amendPaidAt}
                        onChange={e => setAmendPaidAt(e.target.value)}
                        className="w-full bg-gray-50 border border-gray-200 px-4 py-3 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-emerald-500 text-gray-900 font-semibold text-sm transition-all"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSaveAmend}
                  disabled={savingAmend || (amendStatus === 'Paid' && (!amendAmount.trim() || !amendPaidBy.trim()))}
                  className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:opacity-50 text-white font-extrabold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition-all shadow hover:shadow-lg"
                >
                  {savingAmend && <Loader className="animate-spin" size={16} />}
                  Save Amendments
                </button>
                <button
                  onClick={() => setShowAmendModal(false)}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-extrabold py-3.5 rounded-2xl transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};
